import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useIsHost } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';
import { SCOREBOARD_MESSAGES, POSITION_LABELS } from '@youraiquiz/shared';

type RevealPhase = 'START' | 'SAFE_PLAYERS' | 'WINNER' | 'LOSER' | 'COMPLETE';

const ScoreboardView = () => {
  const { gameState } = useGameStore();
  const isHost = useIsHost();
  const { goToPenalty, kickPlayer } = useGameActions();
  const { leaveRoom } = useSocket();

  const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);
  const winner = sortedPlayers[0];
  const loser = sortedPlayers[sortedPlayers.length - 1];
  const midPlayers = sortedPlayers.slice(1, -1);

  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<RevealPhase>('START');
  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);

  // Animated reveal sequence
  useEffect(() => {
    const sequence = async () => {
      setPhase('START');
      await new Promise(r => setTimeout(r, 1000));

      setPhase('SAFE_PLAYERS');
      for (let i = midPlayers.length - 1; i >= 0; i--) {
        setRevealedIds(prev => new Set([...prev, midPlayers[i].id]));
        await new Promise(r => setTimeout(r, 600));
      }
      await new Promise(r => setTimeout(r, 1000));

      setPhase('WINNER');
      setRevealedIds(prev => new Set([...prev, winner.id]));
      await new Promise(r => setTimeout(r, 2000));

      setPhase('LOSER');
      setRevealedIds(prev => new Set([...prev, loser.id]));
      await new Promise(r => setTimeout(r, 1000));

      setPhase('COMPLETE');
    };

    sequence();
  }, []);

  const handleKickClick = (id: string) => {
    if (confirmKickId === id) {
      kickPlayer(id);
      setConfirmKickId(null);
    } else {
      setConfirmKickId(id);
      setTimeout(() => setConfirmKickId(null), 3000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-2xl space-y-8 pb-32"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-5xl md:text-7xl font-black italic uppercase tracking-tighter mb-2">
          FINAL <span className="text-neon-purple">STANDINGS</span>
        </h2>
        <p className="text-gray-500 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">
          {phase === 'START' && SCOREBOARD_MESSAGES.calibrating}
          {phase === 'SAFE_PLAYERS' && SCOREBOARD_MESSAGES.revealing}
          {phase === 'WINNER' && SCOREBOARD_MESSAGES.champion}
          {phase === 'LOSER' && SCOREBOARD_MESSAGES.sacrifice}
          {phase === 'COMPLETE' && SCOREBOARD_MESSAGES.done}
        </p>
      </div>

      {/* Player Cards */}
      <div className="space-y-4">
        <AnimatePresence mode="popLayout">
          {sortedPlayers.map((p, i) => {
            if (!revealedIds.has(p.id)) return null;

            const isWinner = p.id === winner.id;
            const isLoser = p.id === loser.id;
            const isConfirming = confirmKickId === p.id;
            const rank = i + 1;

            return (
              <motion.div
                layout
                key={p.id}
                initial={{ x: isWinner ? -100 : isLoser ? 100 : 0, scale: 0.8, opacity: 0 }}
                animate={{ x: 0, scale: isWinner ? 1.1 : isLoser ? 1.05 : 1, opacity: 1 }}
                className={`glass p-6 rounded-2xl flex items-center justify-between border-l-8 relative group transition-all ${
                  isWinner
                    ? 'border-yellow-500 bg-yellow-500/10 shadow-[0_0_40px_rgba(234,179,8,0.3)] z-20'
                    : isLoser
                    ? 'border-red-600 bg-red-600/10 shadow-[0_0_40px_rgba(220,38,38,0.3)] z-30'
                    : 'border-neon-blue opacity-80'
                } ${isConfirming ? 'border-red-600 bg-red-600/10' : ''}`}
              >
                {/* Rank Badge */}
                <div
                  className={`absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center font-black text-xl shadow-xl ${
                    isWinner
                      ? 'bg-yellow-500 text-black'
                      : isLoser
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-800 text-white'
                  }`}
                >
                  {rank}
                </div>

                {/* Player Info */}
                <div className="flex items-center gap-6 ml-6">
                  <div className="relative">
                    <span className="text-5xl">{p.avatar}</span>
                    {isWinner && (
                      <motion.span
                        animate={{ y: [-10, -15, -10], rotate: [0, 10, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="absolute -top-6 left-1/2 -translate-x-1/2 text-3xl"
                      >
                        👑
                      </motion.span>
                    )}
                    {isLoser && (
                      <motion.span
                        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="absolute -top-6 left-1/2 -translate-x-1/2 text-3xl"
                      >
                        ⚠️
                      </motion.span>
                    )}
                  </div>
                  <div>
                    <h3
                      className={`text-2xl font-black ${
                        isWinner ? 'text-yellow-500' : isLoser ? 'text-red-500' : 'text-white'
                      }`}
                    >
                      {p.name}
                    </h3>
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                      {isWinner
                        ? POSITION_LABELS.winner
                        : isLoser
                        ? POSITION_LABELS.loser
                        : POSITION_LABELS.middle}
                    </p>
                  </div>
                </div>

                {/* Score */}
                <div className="text-right flex items-center gap-4">
                  <div>
                    <span
                      className={`text-4xl font-black ${
                        isWinner ? 'text-yellow-500' : isLoser ? 'text-red-500' : 'text-neon-blue'
                      }`}
                    >
                      {p.score}
                    </span>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Points</p>
                  </div>
                  {isHost && p.id !== gameState.hostId && phase === 'COMPLETE' && (
                    <button
                      onClick={() => handleKickClick(p.id)}
                      className={`h-8 px-2 rounded-full flex items-center justify-center font-black text-[10px] shadow-lg transition-all ${
                        isConfirming
                          ? 'bg-red-600 text-white w-auto opacity-100'
                          : 'bg-red-600 text-white w-8 opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      {isConfirming ? 'KICK?' : '✕'}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Proceed Button */}
      {phase === 'COMPLETE' && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 flex justify-center z-50 px-4 pb-[max(2rem,env(safe-area-inset-bottom))]"
        >
          {isHost ? (
            <button
              onClick={goToPenalty}
              className="bg-red-600 w-full max-w-md py-6 rounded-3xl font-black text-3xl hover:scale-105 active:scale-95 transition-all shadow-[0_0_50px_rgba(220,38,38,0.5)] border-t-4 border-red-400"
            >
              PROCEED TO PUNISHMENT
            </button>
          ) : (
            <div className="bg-black/80 backdrop-blur-xl p-6 rounded-3xl border border-red-600/30 text-red-500 font-black text-xl italic animate-pulse uppercase tracking-tighter text-center w-full max-w-md">
              Waiting for host to deliver the blow...
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ScoreboardView;
